import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, Gauge, TrendingUp, TrendingDown, CheckCircle2 } from "lucide-react";

export default function AIPlanDialog({ open, onOpenChange, project, onGenerated }) {
  const [scope, setScope] = useState(project?.description || "");
  const [industry, setIndustry] = useState(project?.industry || "");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [adjust, setAdjust] = useState(null);

  const close = (v) => {
    if (!v) setAdjust(null);
    onOpenChange(v);
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/ai/generate-plan`, {
        scope, industry, start_date: startDate,
      });
      const tasks = Array.isArray(data) ? data : data.tasks || [];
      const adj = Array.isArray(data) ? [] : data.velocity_adjustments || [];
      toast.success(`Generated ${tasks.length} tasks across industry-standard process groups`);
      onGenerated(tasks);
      if (adj.length) setAdjust(adj);
      else onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="ai-plan-dialog">
        {adjust ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2"><Gauge className="h-5 w-5 text-primary" /> Auto Pilot calibrated the schedule</DialogTitle>
              <DialogDescription>Task durations were adjusted using each assignee's measured delivery velocity.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5" data-testid="velocity-adjustments">
              {adjust.map((a, i) => (
                <div key={i} className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${a.factor > 1 ? "border-amber-200 bg-amber-50/60" : "border-emerald-200 bg-emerald-50/60"}`} data-testid={`velocity-adjustment-${i}`}>
                  {a.factor > 1 ? <TrendingDown className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" /> : <TrendingUp className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />}
                  <span>
                    <b>{a.task}</b>
                    <span className="block text-muted-foreground mt-0.5">
                      {a.factor > 1 ? `Scheduled ${a.factor}× longer` : `Compressed to ${a.factor}× duration`} ({a.from_days}d → {a.to_days}d) — {a.assignee} is currently rated <b className="capitalize">{a.level}</b> ({a.measured}× planned pace)
                    </span>
                  </span>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={() => close(false)} className="gap-2" data-testid="ai-plan-adjustments-done">
                <CheckCircle2 className="h-4 w-4" /> Got it
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> AI plan builder</DialogTitle>
              <DialogDescription>Describe the scope. We'll draft an industry-tailored plan structured by five industry-standard process groups, with scheduled tasks and dependencies.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="ai-scope">Project scope / goal</Label>
                <Textarea id="ai-scope" rows={4} value={scope} onChange={(e) => setScope(e.target.value)} placeholder="e.g. Launch a mobile banking app for SMB customers within 6 months..." data-testid="ai-scope-input" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ai-ind">Industry</Label>
                  <Input id="ai-ind" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Fintech, Construction..." data-testid="ai-industry-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="ai-start">Start date</Label>
                  <Input id="ai-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="ai-start-input" className="mt-1.5" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Generated tasks are appended to this project. Assignee durations auto-calibrate to measured velocity (Slow = longer, Fast = shorter). Takes ~15-30 seconds.</p>
            </div>
            <DialogFooter>
              <Button onClick={run} disabled={loading || !scope.trim()} className="gap-2" data-testid="ai-generate-submit">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Building plan...</> : <><Sparkles className="h-4 w-4" /> Generate plan</>}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
